import { z } from "zod";
import { PRODUCT } from "~/constants/products";

type ProductName = typeof PRODUCT[keyof typeof PRODUCT];

/** sessionStorage keys: the cookie is httpOnly, so the quiz reads the slug from here. */
export const AFFILIATE_SESSION_KEY = {
    SLUG: "ib_aff_slug",
    REMOVED_COUPONS: "ib_aff_removed_coupons",
    REPORTED_COUPONS: "ib_aff_reported_coupons",
} as const;

/** Keeps the message within two lines beside "Terms apply" at 375px. The longest partner message is 62. */
export const AFFILIATE_MESSAGE_MAX_LENGTH = 65;

// Seconds: a numeric `expires` on ResponseCookies is read as epoch milliseconds.
export const AFFILIATE_COOKIE_MAX_AGE_SECONDS = 30 * 24 * 60 * 60;

/** Quizzes that can show a partner offer, keyed by the slug offers use. */
export const AFFILIATE_QUIZ = {
    HORMONE_IMBALANCE: "hormone-imbalance",
    ANTI_AGING_FACE_CREAM: "anti-aging-face-cream",
    BIOIDENTICAL_HRT: "bioidentical-hrt",
} as const;

export type AffiliateQuizSlug = typeof AFFILIATE_QUIZ[keyof typeof AFFILIATE_QUIZ];

/**
 * Products a quiz sells, so checkout can find the offer for the cart. Checkout
 * doesn't know which quiz the visitor took, so when two quizzes sell a product
 * the first one listed with a usable offer gives its code.
 */
export const AFFILIATE_QUIZ_PRODUCTS: Record<AffiliateQuizSlug, readonly ProductName[]> = {
    [AFFILIATE_QUIZ.HORMONE_IMBALANCE]: [PRODUCT.OESTRA_CREAM],
    [AFFILIATE_QUIZ.ANTI_AGING_FACE_CREAM]: [PRODUCT.FACE_CREAM, PRODUCT.FACE_CREAM_FINASTERIDE],
    [AFFILIATE_QUIZ.BIOIDENTICAL_HRT]: [PRODUCT.OESTRA_CREAM],
};

const affiliateOfferSchema = z.object({
    code: z.string().min(1),
    discount: z.number(),
    publicOffer: z.number(),
});

export const affiliatePartnerSchema = z.object({
    enabled: z.boolean(),
    displayName: z.string(),
    utmCampaignId: z.string().regex(/^\d+$/),
    message: z.string().min(1).max(AFFILIATE_MESSAGE_MAX_LENGTH),
    offers: z.record(z.string(), affiliateOfferSchema),
});

/** The store's items, each then validated as a partner, so one bad entry doesn't take the others offline. */
export const affiliateStoreItemsSchema = z.record(z.string(), z.unknown());

export type AffiliateOffer = z.infer<typeof affiliateOfferSchema>;
export type AffiliatePartner = z.infer<typeof affiliatePartnerSchema>;
export type AffiliatePartners = Record<string, AffiliatePartner>;

export const AFFILIATE_LOG_EVENT = {
    CONFIG_MISSING: "affiliate_config_missing",
    CONFIG_READ_FAILED: "affiliate_config_read_failed",
    CONFIG_INVALID: "affiliate_config_invalid",
    OFFER_BELOW_PUBLIC: "affiliate_offer_below_public_offer",
    PRECOMPUTE_FAILED: "affiliate_precompute_failed",
    OFFER_OVER_BUDGET: "affiliate_offer_over_code_budget",
} as const;

export const AFFILIATE_EVENT = {
    BANNER_SHOWN: "affiliate_banner_shown",
    BANNER_UNMATCHED: "affiliate_banner_unmatched",
    COUPON_AUTO_APPLIED: "affiliate_coupon_auto_applied",
    COUPON_SKIPPED: "affiliate_coupon_skipped",
    COUPON_REJECTED: "affiliate_coupon_rejected",
} as const;
